import {
  EffectCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

/**
 * Reference:
 * https://github.com/streamich/react-use
 */

/**
 * React lifecycle hook that runs an effect only once.
 * -
 * Use:
 *
  useEffectOnce(() => {
    console.log('Running effect once on mount')

    return () => {
      console.log('Running clean-up of effect on unmount');
    };
  });
 *
 * @deprecated Do not use in new code. This is `useEffect(fn, [])` behind another
 * name, so `react-hooks-extra/no-direct-set-state-in-use-effect` and
 * `react/react-compiler` — both errors repo-wide — cannot see the effect at all.
 * That blind spot is how ZOE-12420 shipped a fetch-and-setState mount effect
 * with zero lint warnings, and `zoe/no-set-state-in-mount-effect` now exists
 * only to cover for it.
 * -
 * Replace with one of the patterns in CLAUDE.md ("React useEffect Policy"):
 * derive during render, call from the event handler, `key` to remount, or fetch
 * through a `@zoefin/services` hook. A cleanup-only mount effect (subscribe /
 * unsubscribe to an external system) is the one honest use left — put that in a
 * named hook under `src/packages/hooks/`, not here.
 */

export const useEffectOnce = (effect: EffectCallback): void => {
  const finalEffect = useRef(effect);

  useEffect(() => {
    return finalEffect.current();
  }, []);
};

/**
 * React lifecycle hook that calls a function after the component is mounted.
 * -
 * Use:
 * useMount(() => alert('MOUNTED'));
 *
 * @deprecated Do not use in new code. Same blind spot as {@link useEffectOnce},
 * which it wraps, minus the cleanup return — so it cannot even do the one thing
 * a mount effect is legitimately for. See {@link useEffectOnce} for the
 * replacements.
 */

export const useMount = (fn: () => void): void => {
  useEffectOnce(() => {
    fn();
  });
};

/**
 * Runs `onChange` when `value` changes, handing it the previous value.
 * -
 * Replaces the old `usePrevious`, which returned the previous value and so had
 * to read a ref while rendering — unsafe, because React can discard a render
 * and leave the ref describing a commit that never happened. Here the ref is
 * read and written inside the same effect, which is where that is legal.
 * -
 * Fires on mount with `previous` as `undefined`, matching what callers used to
 * get from `usePrevious` on the first render — except when `value` is itself
 * `undefined` on mount, since then there is no change to report and the first
 * call waits for a real value. Pass `areEqual` when the value needs a deep
 * comparison rather than reference equality.
 * -
 * Use:
 * useOnChange(pathname, () => datadogRum.startView(pathname));
 * useOnChange(advice, (previous) => sync(previous), isEqual);
 */

export const useOnChange = <T>(
  value: T,
  onChange: (previous: T | undefined) => void,
  areEqual: (previous: T | undefined, next: T) => boolean = Object.is,
): void => {
  const previous = useRef<T | undefined>(undefined);
  const runChange = useEffectEvent((before: T | undefined) => {
    onChange(before);
  });

  useEffect(() => {
    if (areEqual(previous.current, value)) return;

    const before = previous.current;

    previous.current = value;
    runChange(before);
  }, [value, areEqual]);
};

/**
 * The previous *distinct* value, safe to read while rendering.
 * -
 * Uses React's documented "adjust state during render" pattern, so nothing is
 * read from a ref mid-render. Note the difference from {@link useOnChange}:
 * this settles on the last value that actually changed and is read while
 * rendering; `useOnChange` instead calls you back from an effect. Reach for
 * this when the previous value is needed to render, as TableTd's animated cell
 * does.
 * -
 * Use:
 * const previousLabel = usePreviousValue(label);
 */

export const usePreviousValue = <T>(value: T): T | undefined => {
  const [tracked, setTracked] = useState<{current: T; previous?: T}>({
    current: value,
  });

  /*
   * Object.is, not `!==`: `NaN !== NaN` is true, so a NaN value would set state
   * on every render and hit React's re-render limit. A cell whose number failed
   * to parse is enough to trigger it, and this hook renders table cells.
   */
  if (!Object.is(tracked.current, value)) {
    setTracked({current: value, previous: tracked.current});

    return tracked.current;
  }

  return tracked.previous;
};
